export type PhysicsCallback<T> = (this: T, time: number, dt: number) => any;

export class MatterPhysicsUpdater {
  protected scene: Phaser.Scene;
  protected timeStep: number;
  protected accumulator: number;
  protected matterTime: number;
  protected updateMethods: PhysicsListener<any>[];

  constructor(scene: Phaser.Scene, timeStep: number = 16.66) {
    this.scene = scene;
    this.timeStep = timeStep;
    this.accumulator = 0;
    this.matterTime = 0;

    this.updateMethods = [];

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  get TimeStep() {
    return this.timeStep;
  }

  public add<T = any>(fn: PhysicsCallback<T>, context?: T, priority: number = 0): this {
    this.updateMethods.push(new PhysicsListener(fn, context, priority, false));

    return this;
  }

  public update(time: number, delta: number) {
    this.accumulator += delta;

    this.matterTime = time;
    while (this.accumulator >= this.timeStep) {
      this.accumulator -= this.timeStep;
      this.matterTime += this.timeStep;

      // Update all physics methods.
      for (let i = 0; i < this.updateMethods.length; i++) {
        const listener = this.updateMethods[i];
        listener.emit(this.matterTime, this.timeStep);
      }

      this.scene.matter.world.step(this.timeStep);
    }
  }

  public shutdown() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.updateMethods = [];
  }
}

export class PhysicsListener<T = any> {
  public priority: number;

  private fn: PhysicsCallback<T> | undefined;
  private context: T | undefined;
  private once: boolean;
  private isDestroyed: boolean;

  constructor(fn: PhysicsCallback<T>, context: T | undefined, priority: number, once: boolean) {
    this.fn = fn;
    this.context = context;
    this.priority = priority;
    this.once = once;

    this.isDestroyed = false;
  }

  emit(time: number, delta: number) {
    if (this.fn) {
      if (this.context) {
        this.fn.call(this.context, time, delta);
      } else {
        (this as PhysicsListener<any>)?.fn?.(time, delta);
      }
    }

    if (this.once) this.destroy(true);
  }

  destroy(hard: boolean = false) {
    this.isDestroyed = true;
    this.fn = undefined;
    this.context = undefined;
  }
}
